/**
 * Space Custom Instructions Editor
 *
 * Shared field for editing Space custom instructions (Create Space → Context,
 * Settings → Context) with optional Generate from brief.
 *
 * Primary responsibilities:
 * - Edit custom instructions text with character count
 * - Generate instructions from a short brief + Space name/description
 * - Show a restrained loading overlay while generating
 *
 * Dependencies:
 * - authenticatedFetch
 * - UI button / popover / textarea
 */

"use client"

import { FC, useState } from "react"
import { IconSparkle2 } from "@tabler/icons-react"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover"
import { Textarea } from "@/components/ui/textarea"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import { cn } from "@/lib/utils"

export const SPACE_CUSTOM_INSTRUCTIONS_FIELD_MAX = 2000

type SpaceCustomInstructionsEditorProps = {
  value: string
  onChange: (value: string) => void
  workspaceId: string
  spaceName: string
  spaceDescription: string
  disabled?: boolean
  id?: string
  className?: string
}

const getGenerationResponseError = async (
  response: Response,
  fallback: string
) => {
  const payload = (await response.json().catch(() => null)) as
    | { error?: string }
    | null

  if (payload?.error?.trim()) {
    return payload.error
  }

  return fallback
}

const GeneratingOverlay: FC = () => {
  return (
    <div
      aria-live="polite"
      aria-busy="true"
      className="bg-background/90 absolute inset-0 z-10 flex flex-col justify-center gap-3 rounded-md border p-4"
    >
      <div className="text-foreground flex items-center gap-2 text-xs font-medium">
        <Loader2 className="text-primary size-3.5 animate-spin" />
        Generating custom instructions…
      </div>
      <div className="space-y-2" aria-hidden="true">
        <div className="bg-muted h-2.5 w-11/12 animate-pulse rounded-full" />
        <div className="bg-muted h-2.5 w-9/12 animate-pulse rounded-full" />
        <div className="bg-muted h-2.5 w-10/12 animate-pulse rounded-full" />
      </div>
    </div>
  )
}

export const SpaceCustomInstructionsEditor: FC<
  SpaceCustomInstructionsEditorProps
> = ({
  value,
  onChange,
  workspaceId,
  spaceName,
  spaceDescription,
  disabled = false,
  id = "customInstructions",
  className
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [generationPrompt, setGenerationPrompt] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)

  const trimmedValue = value.trim()
  const characterCount = value.length
  const canGenerateFromContext =
    Boolean(generationPrompt.trim()) ||
    Boolean(spaceName.trim()) ||
    Boolean(spaceDescription.trim())

  const handleGenerate = async () => {
    if (!workspaceId) {
      toast.error("No workspace selected")
      return
    }

    if (!canGenerateFromContext) {
      toast.error("Add a Space name, description, or generation request first")
      return
    }

    setIsGenerating(true)
    setIsPopoverOpen(false)

    try {
      const response = await authenticatedFetch(
        "/api/spaces/generate-custom-instructions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            fileNames: [],
            spaceDescription: spaceDescription.trim(),
            spaceName: spaceName.trim(),
            userDescription: generationPrompt.trim(),
            workspaceId
          })
        }
      )

      if (!response.ok) {
        throw new Error(
          await getGenerationResponseError(
            response,
            "Failed to generate custom instructions"
          )
        )
      }

      const payload = (await response.json()) as {
        instructions?: string
      }

      if (!payload.instructions?.trim()) {
        throw new Error("Failed to generate custom instructions")
      }

      onChange(
        payload.instructions
          .trim()
          .slice(0, SPACE_CUSTOM_INSTRUCTIONS_FIELD_MAX)
      )
      setGenerationPrompt("")
      toast.success("Custom instructions generated")
    } catch (error) {
      console.error("Error generating custom instructions:", error)
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to generate custom instructions"
      )
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <Label htmlFor={id}>Custom Instructions</Label>
          <p className="text-muted-foreground text-xs">
            Included in the system prompt for all chats in this Space. Leave
            empty for none.
          </p>
        </div>

        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || isGenerating}
              className="min-h-10 shrink-0 touch-manipulation sm:min-h-9"
            >
              {isGenerating ? (
                <Loader2 className="mr-2 size-3.5 animate-spin" />
              ) : (
                <IconSparkle2 className="mr-2 size-3.5" />
              )}
              Generate from brief
            </Button>
          </PopoverTrigger>

          <PopoverContent
            align="end"
            className="w-[min(24rem,calc(100vw-2rem))] space-y-3"
          >
            <div className="space-y-1">
              <p className="text-sm font-medium">Generate custom instructions</p>
              <p className="text-muted-foreground text-xs">
                Describe goals, tone, outputs, or what Losi should ask before
                acting. Uses this Space&apos;s name and description as context.
              </p>
            </div>

            <Textarea
              value={generationPrompt}
              onChange={event => setGenerationPrompt(event.target.value)}
              placeholder="Example: Stay execution-focused, treat attached specs as source of truth, and flag launch blockers early."
              rows={4}
              disabled={disabled || isGenerating}
            />

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="min-h-10 touch-manipulation sm:min-h-9"
                disabled={disabled || isGenerating}
                onClick={() => setIsPopoverOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="min-h-10 touch-manipulation sm:min-h-9"
                disabled={disabled || isGenerating || !canGenerateFromContext}
                onClick={() => void handleGenerate()}
              >
                {isGenerating ? (
                  <Loader2 className="mr-2 size-3.5 animate-spin" />
                ) : (
                  <IconSparkle2 className="mr-2 size-3.5" />
                )}
                Generate
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="relative">
        <Textarea
          id={id}
          value={value}
          onChange={event =>
            onChange(
              event.target.value.slice(0, SPACE_CUSTOM_INSTRUCTIONS_FIELD_MAX)
            )
          }
          placeholder="Add specific instructions for how AI should behave in this space context..."
          rows={7}
          maxLength={SPACE_CUSTOM_INSTRUCTIONS_FIELD_MAX}
          disabled={disabled || isGenerating}
          className="min-h-[10rem] resize-y"
        />
        {isGenerating ? <GeneratingOverlay /> : null}
      </div>

      <div className="text-muted-foreground flex items-center justify-between gap-3 text-xs">
        <span>
          {trimmedValue
            ? "Saved on Save Changes. Applied to the next Space chat."
            : "No custom instructions set."}
        </span>
        <span className="tabular-nums">
          {characterCount}/{SPACE_CUSTOM_INSTRUCTIONS_FIELD_MAX}
        </span>
      </div>
    </div>
  )
}
